import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import LocationApi from 'api/LocationApi';
import { showNotification } from 'app-redux/notification.saga';
import * as dto from 'client/dto';

import { updateAoiLayer } from 'map/map.saga';

import { closePanel } from 'diseaseActivityPanels/diseaseActivityPanels.saga';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import { PanelType } from 'diseaseActivityPanels/diseaseActivityPanels.slice';

import locationsSelectors from './locations.selectors';
import {
  addLocationError,
  addLocationStart,
  deleteLocationError,
  deleteLocationStart,
  editLocationsEnd,
  loadAllLocationsError,
  loadAllLocationsStart,
  loadAllLocationsSuccess,
  updateLocationsError,
  updateLocationsStart,
} from './locations.slice';

export const fetchLocations = createAction('locations/fetchLocations');
export const updateLocations = createAction<number[]>('locations/updateLocations');
export const addLocation = createAction<number>('locations/addLocation');
export const deleteLocation = createAction<number>('locations/deleteLocation');

export function* fetchLocationsSaga() {
  try {
    yield put(loadAllLocationsStart());
    const response: dto.GetUserLocationModel = yield call(LocationApi.getUserLocations);
    yield put(loadAllLocationsSuccess(response.geonames));
    yield put(updateAoiLayer());

    const isSelectedLocationInvalid: boolean = yield select(
      locationsSelectors.isSelectedLocationInvalid
    );
    if (isSelectedLocationInvalid) {
      yield put(
        showNotification({
          type: 'SHOW_NEUTRAL_NOTIFICATION',
          payload: `Selected location is not present in the current list`,
        })
      );
      yield put(closePanel(PanelType.DiseasesPanel));
    }
  } catch (e) {
    yield put(loadAllLocationsError());
  } finally {
    yield put({ type: 'FETCH_LOCATIONS_FINISH' });
  }
}

export function* updateLocationsSaga(action: PayloadAction<number[]>) {
  try {
    yield put(updateLocationsStart());
    const response: dto.GetUserLocationModel = yield call(
      LocationApi.updateUserLocations,
      action.payload
    );
    yield put(editLocationsEnd());
    yield put(loadAllLocationsSuccess(response.geonames));
    yield put(updateAoiLayer());
  } catch (e) {
    yield put(updateLocationsError());
  }
}

export function* addLocationSaga(action: PayloadAction<number>) {
  try {
    yield put(addLocationStart());
    const response: dto.GetUserLocationModel = yield call(
      LocationApi.addUserLocation,
      action.payload
    );
    yield put(loadAllLocationsSuccess(response.geonames));
    yield put(updateAoiLayer());
  } catch (e) {
    yield put(addLocationError());
  }
}

export function* deleteLocationSaga(action: PayloadAction<number>) {
  try {
    const deletionId = action.payload;
    const selectedLocationId: number = yield select(
      diseaseActivityPanelsSelectors.getSelectedLocationId
    );

    yield put(deleteLocationStart(deletionId));
    const response: dto.GetUserLocationModel = yield call(
      LocationApi.deleteUserLocation,
      deletionId
    );
    yield put(loadAllLocationsSuccess(response.geonames));
    yield put(updateAoiLayer());
    if (deletionId === selectedLocationId) yield put(closePanel(PanelType.DiseasesPanel));
  } catch (e) {
    yield put(deleteLocationError());
  }
}

function* locationsSaga() {
  yield takeLatest(fetchLocations, fetchLocationsSaga);
  yield takeLatest(updateLocations, updateLocationsSaga);
  yield takeLeading(addLocation, addLocationSaga);
  yield takeLeading(deleteLocation, deleteLocationSaga);
}

export default locationsSaga;
